import React, { useState } from 'react';
import {
  Button,
  VStack,
  Input,
  Text,
  Field,
} from '@chakra-ui/react';
import { RadioGroup, Radio } from '../components/ui/radio';
import {
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '../components/ui/modal';
import { SimpleSigner, ExtensionSigner } from 'applesauce-signers/signers';
import { generateSecretKey, getPublicKey, nip19 } from 'nostr-tools';

interface NewIdentityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (identity: {
    pubkey: string;
    nsec?: string;
    source: 'generated' | 'nsec' | 'signer';
  }) => void;
}

import { toaster } from '../components/ui/toaster';

function NewIdentityModal({ isOpen, onClose, onSave }: NewIdentityModalProps) {
  const [method, setMethod] = useState<'generate' | 'nsec' | 'extension'>('generate');
  const [nsecInput, setNsecInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Generate new key pair
      const secretKey = generateSecretKey();
      const pubkey = getPublicKey(secretKey);
      const nsec = nip19.nsecEncode(secretKey);
      
      onSave({
        pubkey,
        nsec,
        source: 'generated',
      });
      
      toaster.create({
        title: 'Identity created',
        description: 'New keys generated successfully',
        type: 'success',
        duration: 3000,
      });
      
      handleClose();
    } catch (err) {
      setError('Failed to generate keys');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNsec = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      if (!nsecInput.startsWith('nsec1')) {
        setError('Invalid nsec format');
        return;
      }
      
      // Decode nsec
      const decoded = nip19.decode(nsecInput);
      if (decoded.type !== 'nsec') {
        setError('Invalid nsec');
        return;
      }
      
      const secretKey = decoded.data as Uint8Array;
      const pubkey = getPublicKey(secretKey);
      
      onSave({
        pubkey,
        nsec: nsecInput,
        source: 'nsec',
      });
      
      toaster.create({
        title: 'Identity imported',
        description: 'Keys imported successfully',
        type: 'success',
        duration: 3000,
      });
      
      handleClose();
    } catch (err) {
      setError('Failed to import nsec');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtension = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Check if extension is available
      if (!window.nostr) {
        setError('No Nostr extension found. Please install Alby, nos2x, or another Nostr signer extension.');
        return;
      }
      
      // Get public key from extension
      const signer = new ExtensionSigner();
      const pubkey = await signer.getPublicKey();
      
      onSave({
        pubkey,
        source: 'signer',
      });
      
      toaster.create({
        title: 'Connected to extension',
        description: 'Successfully connected to Nostr extension',
        type: 'success',
        duration: 3000,
      });
      
      handleClose();
    } catch (err) {
      setError('Failed to connect to extension');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    switch (method) {
      case 'generate':
        handleGenerate();
        break;
      case 'nsec':
        handleNsec();
        break;
      case 'extension':
        handleExtension();
        break;
    }
  };

  const handleClose = () => {
    setMethod('generate');
    setNsecInput('');
    setError('');
    setIsLoading(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalHeader>New Identity</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
          <VStack gap={4} align="stretch">
            <RadioGroup.Root value={method} onValueChange={(e) => setMethod(e.value as any)}>
              <VStack align="stretch" gap={3}>
                <Radio value="generate">
                  <VStack align="start" gap={0}>
                    <Text fontWeight="medium">Generate keys</Text>
                    <Text fontSize="sm" color="gray.500">
                      Generate new Nostr keys
                    </Text>
                  </VStack>
                </Radio>
                
                <Radio value="nsec">
                  <VStack align="start" gap={0}>
                    <Text fontWeight="medium">Enter nsec</Text>
                    <Text fontSize="sm" color="gray.500">
                      Use existing Nostr keys
                    </Text>
                  </VStack>
                </Radio>
                
                <Radio value="extension">
                  <VStack align="start" gap={0}>
                    <Text fontWeight="medium">Login with extension</Text>
                    <Text fontSize="sm" color="gray.500">
                      Use Nostr web extension
                    </Text>
                  </VStack>
                </Radio>
              </VStack>
            </RadioGroup.Root>

            {method === 'nsec' && (
              <Field.Root invalid={!!error}>
                <Field.Label>Enter your nsec</Field.Label>
                <Input
                  type="password"
                  placeholder="nsec1..."
                  value={nsecInput}
                  onChange={(e) => setNsecInput(e.target.value)}
                />
                <Field.ErrorText>{error}</Field.ErrorText>
              </Field.Root>
            )}

            {method === 'extension' && error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            {method === 'generate' && (
              <Text fontSize="sm" color="orange.500">
                ⚠️ Important: Save your nsec securely. You'll need it to recover your identity.
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            colorPalette="blue" 
            onClick={handleSubmit}
            loading={isLoading}
          >
            {method === 'generate' ? 'Generate' :
             method === 'nsec' ? 'Import' :
             'Connect'}
          </Button>
        </ModalFooter>
    </Modal>
  );
}

// Add type declaration for window.nostr
declare global {
  interface Window {
    nostr?: any;
  }
}

export default NewIdentityModal;